import { defineStore } from "pinia";
import _ from "lodash";
import dayjs from "dayjs";
import type { Table } from "~/interface/Table";
import type { ReceiptOrderDynamodb } from "~/interface/ReceiptOrder";
import { ReceiptOrderClass } from "~/class/ReceiptOrder";
import { child, onChildAdded, onValue, set } from "firebase/database";

type PrintPreviewRequest = {
  tableAlias?: string;
  alias?: string;
  tableName?: string;
  name?: string;
  uuid?: string;
};

export const usePosPrintPreviewStore = defineStore("pos-print-preview", () => {
  const jobsStore = usePosJobsStore();
  const employeeStore = usePosEmployeesStore();
  const shopStore = usePosShopStore();

  const getTableForPreview = (tableAlias: string) => {
    if (!tableAlias) {
      return null;
    }

    const tables = jobsStore.tables as Record<string, any>;
    return tables[tableAlias] || null;
  };

  const buildPreviewPayloadFromTable = (tableAlias: string, overrideUser?: string | null): ReceiptOrderDynamodb | null => {
    const table = getTableForPreview(tableAlias);
    if (!table) {
      return null;
    }

    const tableCloned: Table = _.cloneDeep(table);
    tableCloned.items = table.items || [];
    tableCloned.billing = table.billing;
    tableCloned.endDateTime = dayjs().format("YYYY-MM-DDTHH:mm:ss");

    // For a terminal-triggered remote preview, `overrideUser` carries the employee
    // PIN'd on the TERMINAL — the person who actually requested the bill. Prefer it
    // over this cashier's own PIN so the printed Cashier matches the terminal
    // operator. Falls back to the local employee when absent (local preview / old
    // terminal build that sent no user).
    return ReceiptOrderClass
      .fromTable(tableCloned, {
        orderId: "",
        shiftWork: jobsStore.getShift?.() || "Shift 1",
        user: overrideUser || employeeStore.getCurrentEmployee?.name || "admin",
        payment: { list: [] },
        customerId: shopStore.getStoreId,
      })
      .toDynamodb();
  };

  const printPreviewByTable = (tableAlias: string, overrideUser?: string | null): ReceiptOrderDynamodb | null => {
    return buildPreviewPayloadFromTable(tableAlias, overrideUser);
  };

  const resolveRequestTableAlias = (payload: PrintPreviewRequest | string) => {
    if (typeof payload === "string") {
      return payload;
    }

    return payload.tableAlias
      || payload.alias
      || payload.tableName
      || payload.name
      || payload.uuid
      || "";
  };

  const handleTerminalRequest = ( ) => {

    // The terminal writes a CHILD keyed by alias (sendPrintPreviewRequest →
    // child(ref, alias)). Value is { alias, user } on current builds, or a bare
    // alias string on older ones (handled below). Listen per-child, then clear
    // that child. Mirrors watchOrderFromTerminal in pos.terminal.ts.
    const printPreviewRef = useRealtimeDatabase().printPreviewFromTerminalRef()
    onChildAdded(printPreviewRef, (snapshot) => {

      const value = snapshot.val();

      if (value == null) {
        return
      }

      // Back-compat: older terminals write a bare alias string; newer ones write
      // { alias, user }. Accept both — pull the terminal operator's name when present
      // so the preview prints with the terminal's cashier, not this device's PIN.
      const tableAlias = typeof value === "string" ? value : value.alias;
      const overrideUser = typeof value === "string" ? undefined : value.user;

      if (!tableAlias) {
        set(snapshot.ref, null)
        return
      }

      usePosPrintPreviewStore().printPreviewByTable(tableAlias, overrideUser)

      //clear data
      set(snapshot.ref, null)
    });

  };

  // requestPayment is written by the EXTERNAL new-mobile app — nothing in this
  // repo writes it, so its retry behavior cannot be relied on. The cashier's
  // only ack is deleting the node (the `!value` early-return below is the brake
  // against our own null-write echo — this is the one onValue in the app that
  // writes the node it listens on). The per-uuid cooldown caps a misbehaving
  // phone at ONE clearing write per round instead of the ~6-10 writes the
  // setLockTable fan-out used to cost (tables_state → recompute → tables_v3 +
  // customercounts + per-table publish).
  const REQUEST_PAYMENT_COOLDOWN_MS = 10_000  // > the phone's 8s retry window
  const lastRequestPaymentAt: Record<string, number> = {}

  const handleMobileNewAppRequest = ( ) => {
    const mobielV1Store = usePosMobileV1Store()
    const tableRef = useRealtimeDatabase().tablesRef()
    const allTableToUuid = mobielV1Store.tableToUuid
    for (const [table, uuid] of Object.entries(allTableToUuid)) {
      // console.log(table, uuid)

      const printPreviewRef = child(tableRef, `${uuid}/requestPayment`)
      onValue(printPreviewRef, (snapshot) => {

        const value = snapshot.val()
        if (!value) {
          return
        }
        const previewStore = usePosPrintPreviewStore()

        const tableUuid = snapshot.ref.parent?.key || ""

        const now = Date.now()
        if (now - (lastRequestPaymentAt[tableUuid] || 0) < REQUEST_PAYMENT_COOLDOWN_MS) {
          set(snapshot.ref, null)
          return
        }
        lastRequestPaymentAt[tableUuid] = now

        const tableAlias = mobielV1Store.convertUuidToTable(tableUuid)
        if (!tableAlias) {
          // Unknown uuid — clear and bail (mirrors handleTerminalRequest).
          // Passing null on used to write a junk tables_state/null/lock key.
          set(snapshot.ref, null)
          return
        }
        previewStore.printPreviewByTable(tableAlias)
        if (!usePosTableStatesStore().isLockedWithTable(tableAlias)) {
          usePosTableStatesStore().setLockTable(tableAlias, true)
        }

        //clear data
        set(snapshot.ref, null)
      })
    }
  };

  const handleMobileOldAppRequest = ( ) => {
    // do the same as handleMobileNewAppRequest
  };

  const init = () => {
    handleTerminalRequest()
    handleMobileNewAppRequest()
    handleMobileOldAppRequest()
  }

  return {
    init,
    getTableForPreview,
    buildPreviewPayloadFromTable,
    printPreviewByTable,
    handleTerminalRequest,
    handleMobileNewAppRequest,
    handleMobileOldAppRequest,
  };
});
